import type {IssueAgentTask, IssueAssignedAgentInfo} from '../contexts/IssueAgentTasksContext'
import type {SessionState} from '@github-ui/agent-sessions/types/session'
import {isSessionState} from '@github-ui/agent-sessions/types/session'
import {getLeadingVisualIconAttributes} from '@github-ui/agent-sessions/utils/get-leading-visual-icon-attributes'
import {getGitHubArtifactData} from '@github-ui/agent-sessions/utils/task-artifact-helpers'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {SimpleListView} from '@github-ui/simple-list-view'
import {SimpleListItem} from '@github-ui/simple-list-view/SimpleListItem'
import {RelativeTime, Stack} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback} from 'react'
import {TASK_NOUN} from '../utils/constants'

import type {LinkedIssuePullRequest} from '../utils/types'
import {getPRStatusIcon} from './PRStatusIcon'
import styles from './TaskList.module.css'

type TaskListProps = {
  tasks: IssueAgentTask[]
  assignedAgents: IssueAssignedAgentInfo[]
  linkedPullRequests: LinkedIssuePullRequest[]
  isNarrow?: boolean
  onSelectTask?: (task: IssueAgentTask) => void
}

function getTaskStateLabel(state: string, hasPR: boolean): string {
  if (state === 'waiting_for_user') return 'Needs input'
  if (state === 'completed') return hasPR ? 'Ready for review' : 'Completed'
  if (state === 'failed' || state === 'timed_out') return 'Failed'
  return 'In progress'
}

function TaskListItem({
  task,
  assignedAgents,
  linkedPullRequests,
  onSelectTask,
}: {
  task: IssueAgentTask
  assignedAgents: IssueAssignedAgentInfo[]
  linkedPullRequests: TaskListProps['linkedPullRequests']
  onSelectTask?: (task: IssueAgentTask) => void
}) {
  const sidePanelEnabled = isFeatureEnabled('issue_cca_task_side_panel')
  const taskState = task.state ?? task.status ?? 'in_progress'
  const state: SessionState = isSessionState(taskState) ? taskState : 'in_progress'
  const title = task.name || `New ${TASK_NOUN}`

  const pullRequestId = getGitHubArtifactData(task.artifacts)?.id
  const pullRequest = linkedPullRequests.find(linkedPr => linkedPr.databaseId === pullRequestId) ?? null
  const leadingVisualAttributes = getLeadingVisualIconAttributes('open', false, state, !!task.archived_at)
  const agentInfo = assignedAgents.find(agent => task.agent_collaborators?.some(c => c.agent_id === agent.id))
  const taskUrl = task.url
  const updatedAt = task.updated_at ?? task.last_updated_at ?? task.created_at

  const handleClick = useCallback(() => {
    onSelectTask?.(task)
  }, [task, onSelectTask])

  return (
    <SimpleListItem className={styles.item}>
      <SimpleListItem.LeadingVisual>
        {leadingVisualAttributes.icon && (
          <div
            style={{
              color: leadingVisualAttributes.color,
            }}
          >
            <leadingVisualAttributes.icon />
          </div>
        )}
      </SimpleListItem.LeadingVisual>
      {/* We have to build a complete Title subcomponent instead of using Description because SimpleListView doesn't allow for LeadingVisual with Description correctly */}
      <SimpleListItem.Title
        {...(sidePanelEnabled ? {onClick: handleClick} : taskUrl ? {href: taskUrl} : {})}
        className={styles.titleColumn}
      >
        <Stack gap="none">
          <span className={styles.title}>{title}</span>
          <span className={styles.description}>
            {getTaskStateLabel(taskState, !!pullRequest)}
            {updatedAt && (
              <>
                {' · '}Updated <RelativeTime tense="past" datetime={updatedAt} format="relative" />
              </>
            )}
          </span>
        </Stack>
      </SimpleListItem.Title>

      <SimpleListItem.Actions className={styles.actionsColumn}>
        <Stack direction="horizontal" align="center" justify="end" gap="normal">
          {pullRequest && (
            <a
              href={pullRequest.url}
              aria-label={`View pull request #${pullRequest.number}`}
              className={styles.pullRequest}
            >
              {getPRStatusIcon(pullRequest.state)}
              <span>#{pullRequest.number}</span>
            </a>
          )}
          {agentInfo?.avatar_url && (
            <GitHubAvatar src={agentInfo?.avatar_url} size={16} square alt={agentInfo?.display_name || 'Agent'} />
          )}
        </Stack>
      </SimpleListItem.Actions>
    </SimpleListItem>
  )
}

export function TaskList({tasks, assignedAgents, linkedPullRequests, isNarrow, onSelectTask}: TaskListProps) {
  return (
    <SimpleListView className={clsx(styles.taskList, isNarrow && styles.narrow)} data-testid="agent-task-list">
      <SimpleListView.Items>
        {tasks.map((task: IssueAgentTask) => (
          <TaskListItem
            key={task.id}
            task={task}
            assignedAgents={assignedAgents}
            linkedPullRequests={linkedPullRequests}
            onSelectTask={onSelectTask}
          />
        ))}
      </SimpleListView.Items>
    </SimpleListView>
  )
}
